import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Input,
  Pagination,
  SelectItem,
  Tab,
  Tabs,
  Tooltip,
  useDisclosure,
} from "@heroui/react";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../utils/toast";
import { ContriesDropdown } from "../../components/common/countriesDropdown";
import AppSelect from "../../components/common/appSelect";
import FighterCard from "./view/fighterCard";
import ViewModal from "./view/viewModal";
import { useReadyRoster } from "./hooks/useReadyRoster";
import {
  createCardView,
  createContact,
  fetchRosterStatus,
  fetchTrainingDisciplines,
  fetchWeightClasses,
  getAllMessages,
  sendMessage,
  updateReadyRosterCardStatus,
} from "../../services/api";
import { CardViewRequestDto, MarketplaceCardResponseDto, ReadyRosterCardDto } from "../../types/types";
import { formatPurseText } from "../../utils/opportunities/opportunityAndReadyRoster";
import { formatEnumString, formatSportName, formatWeightClassName } from "../../utils/utilMethods";
import CreateEditReadyRoster from "./createEdit/createEditReadyRoster";

const ReadyRoster: React.FC = () => {
  const [role, setRole] = useState("");
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewCard, setViewCard] = useState<MarketplaceCardResponseDto | null>(null);
  const [currentMonth, setCurrentMonth] = useState<CalendarDate>(
    today(getLocalTimeZone())
  );
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [showReportPopup, setShowReportPopup] = useState(false);
  const {
    fighters,
    searchLocation,
    setSearchLocation,
    searchName,
    setSearchName,
    loading,
    activeTab,
    handleSportChange,
    handleWeightClassFilterChange,
    handleRosterStatusFilterChange,
    handleRefresh,
    handleDelete,
    handleTabChange,
    fetchData,
    currentPage,
    setCurrentPage,
    totalPages,
  } = useReadyRoster();

  const loginResponse = JSON.parse(localStorage.getItem("loginResponse") || "{}");
  const loggedInUserName = loginResponse.username;
  const loggedInUserProfileType = loginResponse.profileType;

  const { t, i18n } = useTranslation('readyRoster');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const [selectedFighter, setSelectedFighter] = useState<
    MarketplaceCardResponseDto | undefined
  >(undefined);
  const [sports, setSports] = useState<string[]>([]);
  const [selectedSport, setSelectedSport] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [weightClasses, setWeightClasses] = useState<string[]>([]);
  const [rosterStatus, setRosterStatus] = useState<string[]>([]);
  const [selectedRosterStatus, setSelectedRosterStatus] = useState("");
  const [selectedWeight, setSelectedWeight] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [confirmPublishPopup, setConfirmPublishPopup] = useState(false);
  const [confirmContactPopup, setConfirmContactPopup] = useState(false);

  const handleCreate = useCallback(() => {
    setSelectedFighter(undefined);
    onCreateOpen();
  }, [onCreateOpen]);

  const handleEdit = useCallback(
    (fighter: MarketplaceCardResponseDto) => {
      setSelectedFighter(fighter);
      onEditOpen();
    },
    [onEditOpen]
  );

  const handleCreateClose = useCallback(() => {
    setSelectedFighter(undefined);
    onCreateClose();
  }, [onCreateClose]);

  const handleEditClose = useCallback(() => {
    setSelectedFighter(undefined);
    onEditClose();
  }, [onEditClose]);

  const handlePublish = useCallback(
    async (cardId: string) => {
      if (isPublishing) return;

      setIsPublishing(true);
      try {
        const parsedCardId = parseInt(cardId, 10);
        if (isNaN(parsedCardId)) {
          throw new Error("Invalid card ID: must be a valid number");
        }

        await updateReadyRosterCardStatus(parsedCardId);
        setIsViewOpen(false);
        fetchData(activeTab === "myCards");
        toast.success(t("card_published_successfully"));
      } catch (error) {
        setError(t("failed_to_publish_card"));
        toast.error(t("failed_to_publish_card"));
      } finally {
        setIsPublishing(false);
      }
    },
    [fetchData, activeTab, isPublishing, t]
  );

  const trackGeoLocation = async (viewedCardId?: string | number) => {
    try {
      if (!viewedCardId) {
        console.warn("No card ID provided. Skipping tracking.");
        return;
      }

      const storageKey = `lastViewed_${viewedCardId}`;
      const lastViewed = localStorage.getItem(storageKey);

      if (lastViewed && Date.now() - Number(lastViewed) < 5 * 60 * 1000) {
        return;
      }

      const geoRes = await fetch("https://ipapi.co/json");
      const geoData = await geoRes.json();

      const userJson = localStorage.getItem("loginResponse");
      const user = userJson ? JSON.parse(userJson) : null;

      if (!user?.username) {
        return;
      }

      const dto: CardViewRequestDto = {
        readyRosterCardId: viewedCardId,
        informationRequestDto: {
          ipAddress: geoData.ip,
          countryCode: geoData.country_code,
          city: geoData.city,
          browserInfo: navigator.userAgent,
          sourcePage: window.location.pathname,
        },
      };

      await createCardView(user.username, dto);
      localStorage.setItem(storageKey, Date.now().toString());
    } catch (error: any) {
    }
  };

  const handleResetFilters = useCallback(() => {
    setSearchName("");
    setSearchLocation("");
    setSelectedSport("");
    setSelectedWeight("");
    setSelectedRosterStatus("");
    setCurrentPage(0);
    handleSportChange("ALL");
    handleWeightClassFilterChange("ALL");
    handleRosterStatusFilterChange("ALL");
    handleRefresh();
  }, [handleSportChange, handleRosterStatusFilterChange, handleWeightClassFilterChange, handleRefresh]);

  const getUserRole = useCallback((): string => {
    const userJson = localStorage.getItem("loginResponse");
    const user = userJson ? JSON.parse(userJson) : null;
    return user?.profileType || "";
  }, []);

  const loadSportsIfNeeded = useCallback(async () => {
    if (sports.length === 0) {
      try {
        const sportsData = await fetchTrainingDisciplines();
        setSports(sportsData);
      } catch (e) {
        console.error(e);
        setError(t("failed_to_load_training_disciplines"));
      }
    }
  }, [sports.length, t]);

  const loadWeightClassesIfNeeded = useCallback(async () => {
    if (weightClasses.length === 0) {
      try {
        const weightClassesData = await fetchWeightClasses();
        setWeightClasses(weightClassesData);
      } catch (e) {
        console.error(e);
        setError(t("failed_to_load_weight_classes"));
      }
    }
  }, [weightClasses.length, t]);

  const loadRosterStatusesIfNeeded = useCallback(async () => {
    if (rosterStatus.length === 0) {
      try {
        const rosterStatusData = await fetchRosterStatus();
        setRosterStatus(rosterStatusData);
      } catch (e) {
        console.error(e);
        setError(t("failed_to_load_roster_statuses"));
      }
    }
  }, [rosterStatus.length, t]);

  const handleSportSelectionChange = useCallback(
    (key: any) => {
      const sportValue = Array.from(key)[0] as string;
      setSelectedSport(sportValue);
      handleSportChange(sportValue || "ALL");
    },
    [handleSportChange]
  );

  const handleWeightSelectionChange = useCallback(
    (key: any) => {
      const weightValue = Array.from(key)[0] as string;
      setSelectedWeight(weightValue);
      handleWeightClassFilterChange(weightValue || "ALL");
    },
    [handleWeightClassFilterChange]
  );

  const handleRosterStatusChange = useCallback(
    (key: any) => {
      const selectedStatus = Array.from(key)[0] as string;
      setSelectedRosterStatus(selectedStatus);
      handleRosterStatusFilterChange(selectedStatus || "ALL");
    },
    [handleRosterStatusFilterChange]
  );

  const handleTabSelectionChange = useCallback(
    (key: any) => {
      const tabValue = key as "all" | "myCards";
      handleTabChange(tabValue);
    },
    [handleTabChange]
  );

  useEffect(() => {
    setRole(getUserRole());
  }, [getUserRole]);

const handleCreateContact = async (cardId: any, e: any) => {
  e.stopPropagation();

  try {
    const receiverId = viewCard?.readyRosterCard?.fighter?.id;

    const receiverFirstNameAndLastName = `${viewCard?.readyRosterCard?.fighter?.firstName || ""} ${viewCard?.readyRosterCard?.fighter?.lastName || ""}`;

    if (!receiverId) {
      return;
    }

    await getAllMessages(receiverId, "INDIVIDUAL"); 
    const contact = await createContact("ready-roster", cardId);

    if (contact) {
      await sendMessage({
        receiverId,
        message: `Hi ${receiverFirstNameAndLastName}! I would like to contact you regarding your Ready roster for ${formatSportName(viewCard?.readyRosterCard?.sport ?? "")} with a purse of ${formatPurseText(viewCard?.readyRosterCard?.purse ?? "0")}${viewCard?.readyRosterCard?.currency !== "OTHER" ? " " + viewCard?.readyRosterCard?.currency : ""}.`,
        chatType: "INDIVIDUAL",
        type: "CONTENT",
      });
      toast.success(t("contact_invite_success"));
    }

    setIsViewOpen(false);
  } catch (error: any) {
    toast.error(error?.response?.data?.message);
  }
};

  useEffect(() => {
    if (viewCard && isViewOpen && viewCard.readyRosterCard?.availableDays?.length) {
      const datesSet = new Set(viewCard.readyRosterCard?.availableDays);
      setSelectedDates(datesSet);
      const firstDate = `${new Date(viewCard.readyRosterCard?.availableDays[0]).getFullYear()}-${String(new Date(viewCard.readyRosterCard?.availableDays[0]).getMonth() + 1).padStart(2, '0')}-01`;
      setCurrentMonth(
        new CalendarDate(
          new Date(firstDate).getFullYear(),
          new Date(firstDate).getMonth() + 1,
          1
        )
      );
    } else {
      setSelectedDates(new Set());
      setCurrentMonth(today(getLocalTimeZone()));
    }
  }, [viewCard, isViewOpen]);

  const handleView = (item: MarketplaceCardResponseDto) => {
    setViewCard(item);
    setIsViewOpen(true);

    if (activeTab === "all") {
      const cardId = item?.id ? String(item.id) : undefined;
      trackGeoLocation(Number(cardId));
    }
  };

  return (
    <div className="w-full h-90 flex flex-col bg-gray-50">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-2 flex-shrink-0">
        <div className="max-w-full mx-auto">
          {role === "FIGHTER" && (
            <Tabs
              selectedKey={activeTab}
              onSelectionChange={handleTabSelectionChange}
              aria-label={t("roster_tabs")}
              className="mb-2"
            >
              <Tab key="all" title={t("ready_rosters")} />
              <Tab key="myCards" title={t("my_ready_rosters")} />
            </Tabs>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <Input
              placeholder={t("search_by_athlete_name")}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              startContent={
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
              }
              className="w-64 min-w-64"
              size="sm"
            />

            <div className="w-48 min-w-48">
              <ContriesDropdown
                value={searchLocation}
                onLocationChange={setSearchLocation}
              />
            </div>

            <AppSelect
              placeholder={t("select_sport")}
              selectedKeys={selectedSport ? [selectedSport] : []}
              onSelectionChange={handleSportSelectionChange}
              onOpenChange={(isOpen) => isOpen && loadSportsIfNeeded()}
              className="w-48 min-w-48"
              size="sm"
            >
              {sports.length > 0 ? (
                sports.map((sport) => (
                  <SelectItem key={sport}>{formatSportName(sport)}</SelectItem>
                ))
              ) : (
                <SelectItem key="no-sport" isDisabled>
                  {t("no_sports_available")}
                </SelectItem>
              )}
            </AppSelect>

            <AppSelect
              placeholder={t("select_weight_class")}
              selectedKeys={selectedWeight ? [selectedWeight] : []}
              onSelectionChange={handleWeightSelectionChange}
              onOpenChange={(isOpen) => isOpen && loadWeightClassesIfNeeded()}
              className="w-48 min-w-48"
              size="sm"
            >
              {weightClasses.length > 0 ? (
                weightClasses.map((weight) => (
                  <SelectItem key={weight}>{formatWeightClassName(weight)}</SelectItem>
                ))
              ) : (
                <SelectItem key="no-weight" isDisabled>
                  {t("no_weight_classes_available")}
                </SelectItem>
              )}
            </AppSelect>

            <AppSelect
              placeholder={t("select_roster_statuses")}
              selectedKeys={selectedRosterStatus ? [selectedRosterStatus] : []}
              onSelectionChange={handleRosterStatusChange}
              onOpenChange={(isOpen) => isOpen && loadRosterStatusesIfNeeded()}
              className="w-60 min-w-48"
              size="sm"
            >
              {rosterStatus.length > 0 ? (
                rosterStatus.map((rostStatus) => (
                  <SelectItem key={rostStatus}>{formatEnumString(rostStatus)}</SelectItem>
                ))
              ) : (
                <SelectItem key="no-weight" isDisabled>
                  {t("no_roster_statuses_available")}
                </SelectItem>
              )}
            </AppSelect>

            <Tooltip content={t("reset_all_filters")}>
              <Button
                isIconOnly
                variant="light"
                onClick={handleResetFilters}
                className="h-10 w-10 text-gray-600 hover:text-gray-800"
                size="sm"
              >
                <ArrowPathIcon className="w-5 h-5" />
              </Button>
            </Tooltip>

            <div className="flex-1" />

            {role === "FIGHTER" && activeTab === "myCards" && (
              <Button
                color="primary"
                startContent={<PlusIcon className="w-4 h-4" />}
                onPress={handleCreate}
                className="font-medium"
                size="sm"
              >
                {t("create_ready_roster")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-full mx-auto p-2">
          <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))] justify-center">
            {fighters.map((item: MarketplaceCardResponseDto, index: number) => (
  <FighterCard
    key={item.id ?? index}
    item={item}
    role={role}
    activeTab={activeTab}
    onView={handleView}
    onEdit={handleEdit}
    onDelete={handleDelete}
  />
))}
          </div>

          <div className="flex justify-center mt-8">
            <Pagination
              total={totalPages}
              page={currentPage + 1}
              onChange={(page) => setCurrentPage(page - 1)}
              showControls
              showShadow
              color="primary"
              size="sm"
            />
          </div>

          {fighters.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <MagnifyingGlassIcon className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t("no_fighters_found")}
                </h3>
                <p className="text-gray-600 mb-4">
                  {t("no_fighters_found_description")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateEditReadyRoster
        isOpen={isCreateOpen}
        onClose={handleCreateClose}
        initialData={undefined}
        fetchData={fetchData}
      />
      <CreateEditReadyRoster
        isOpen={isEditOpen}
        onClose={handleEditClose}
        initialData={selectedFighter}
        fetchData={fetchData}
      />

      <ViewModal
        isOpen={isViewOpen}
        onClose={() => {
          setIsViewOpen(false);
          fetchData(activeTab === "myCards");
        }}
        viewCard={viewCard}
        role={role}
        activeTab={activeTab}
        loggedInUserProfileType={loggedInUserProfileType}
        loggedInUserName={loggedInUserName}
        currentMonth={currentMonth}
        setCurrentMonth={setCurrentMonth}
        selectedDates={selectedDates}
        isPublishing={isPublishing}
        confirmPublishPopup={confirmPublishPopup}
        setConfirmPublishPopup={setConfirmPublishPopup}
        confirmContactPopup={confirmContactPopup}
        setConfirmContactPopup={setConfirmContactPopup}
        showReportPopup={showReportPopup}
        setShowReportPopup={setShowReportPopup}
        handlePublish={handlePublish}
        handleCreateContact={handleCreateContact}
      />
    </div>
  );
};

export default ReadyRoster;
